import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { clearApiCache } from "@/lib/cache";

const AuthContext = createContext(null);
const USER_KEY = "lxn_user";
function cachedUser() {
  if (!localStorage.getItem("lxn_token")) return null;
  try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
}
function storeUser(u) {
  try { if (u) localStorage.setItem(USER_KEY, JSON.stringify(u)); else localStorage.removeItem(USER_KEY); } catch {}
}

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(cachedUser);
  const [loading, setLoading] = useState(() => !cachedUser());
  const setUser = useCallback((u) => { storeUser(u); setUserState(u); }, []);

  const refresh = useCallback(async () => {
    // Never fire auth checks on public preview routes — they must work without login.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/p/")) {
      setUser(null); setLoading(false); return;
    }
    const token = localStorage.getItem("lxn_token");
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me", { timeout: 15000 });
      setUser(data);
    } catch (e) {
      // Only a rejected token signs out; network blips keep the cached session.
      if (e.response?.status === 401 || !cachedUser()) {
        setUser(null);
        if (e.response?.status === 401) localStorage.removeItem("lxn_token");
      }
    }
    setLoading(false);
  }, [setUser]);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    clearApiCache();
    localStorage.setItem("lxn_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    clearApiCache();
    localStorage.removeItem("lxn_token");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
